import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import messageService from '../services/messageService';
import MessageList from '../components/messages/MessageList';
import MessageViewer from '../components/messages/MessageViewer';
import MessageComposer, { ComposeMode, type InitialComposeRecipient } from '../components/messages/MessageComposer';
import { MessageDetail } from '../types';
import { getDraft, type LocalDraftMessage } from '../services/draftMessageStore';
import { useAuth } from '../hooks/useAuth';

type ViewMode = 'list' | 'view' | 'compose';
type FolderType = 'inbox' | 'sent' | 'credits' | 'debits' | 'archive' | 'trash';

const Messages: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [folder, setFolder] = useState<FolderType>('inbox');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedMailId, setSelectedMailId] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<MessageDetail | undefined>(undefined);
  const [selectedDraft, setSelectedDraft] = useState<LocalDraftMessage | null>(null);
  const [composeMode, setComposeMode] = useState<ComposeMode | undefined>(undefined);
  const [initialComposeRecipients, setInitialComposeRecipients] = useState<InitialComposeRecipient[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Empty trash dialog state
  const [emptyTrashDialogOpen, setEmptyTrashDialogOpen] = useState(false);
  const [emptyTrashLoading, setEmptyTrashLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldCompose = params.get('compose') === '1';
    const toAddress = (params.get('to') || '').trim();
    if (!shouldCompose || !toAddress) return;

    const displayName = (params.get('name') || toAddress).trim();
    const maxPay = Number(params.get('maxPay') || '0');
    const required = Number(params.get('required') || '0');

    setReplyTo(undefined);
    setSelectedDraft(null);
    setComposeMode(undefined);
    setSelectedMailId(null);
    setInitialComposeRecipients([{
      address: toAddress,
      displayName,
      maxPay: Number.isFinite(maxPay) ? maxPay : 0,
      deliveryFeeRequired: Number.isFinite(required) ? required : 0,
      type: 'to',
    }]);
    setViewMode('compose');

    navigate('/messages', { replace: true });
  }, [location.search, navigate]);

  const handleFolderChange = (_event: React.SyntheticEvent, newValue: FolderType) => {
    // If clicking the same folder tab, always go back to list view
    if (newValue === folder) {
      setViewMode('list');
      setSelectedMailId(null);
      setRefreshKey(prev => prev + 1);
    } else {
      // Switching to a different folder
      setFolder(newValue);
      setViewMode('list');
      setSelectedMailId(null);
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleMessageSelect = (mailId: string) => {
    if (mailId.startsWith('draft:')) {
      const draftId = mailId.slice('draft:'.length);
      const draft = user?.username ? getDraft(user.username, draftId) : null;
      if (draft) {
        setReplyTo(undefined);
        setComposeMode(undefined);
        setSelectedDraft(draft);
        setViewMode('compose');
        return;
      }
    }

    setSelectedMailId(mailId);
    setSelectedDraft(null);
    setViewMode('view');
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedMailId(null);
    setReplyTo(undefined);
    setSelectedDraft(null);
    setComposeMode(undefined);
    // Refresh the list to update read status
    setRefreshKey(prev => prev + 1);
  };

  const handleCompose = () => {
    setReplyTo(undefined);
    setSelectedDraft(null);
    setComposeMode(undefined);
    setInitialComposeRecipients([]);
    setViewMode('compose');
  };

  const handleReply = (message: MessageDetail) => {
    setReplyTo(message);
    setComposeMode('reply');
    setViewMode('compose');
  };

  const handleReplyAll = (message: MessageDetail) => {
    setReplyTo(message);
    setComposeMode('replyAll');
    setViewMode('compose');
  };

  const handleForward = (message: MessageDetail) => {
    setReplyTo(message);
    setComposeMode('forward');
    setViewMode('compose');
  };

  const handleDelete = (_mailId: string) => {
    setViewMode('list');
    setSelectedMailId(null);
    // Refresh the list to remove the deleted message
    setRefreshKey(prev => prev + 1);
  };

  const handleSent = () => {
    // Switch to sent folder and refresh to show the new message
    setFolder('sent');
    setSelectedDraft(null);
    setViewMode('list');
    setRefreshKey(prev => prev + 1);
  };

  const handleDraftSaved = () => {
    if (folder === 'sent') {
      setRefreshKey(prev => prev + 1);
    }
  };

  const handleEmptyTrash = async () => {
    setEmptyTrashLoading(true);
    try {
      const result = await messageService.emptyTrash();
      console.log('Empty trash result:', result);
      setEmptyTrashDialogOpen(false);
      setRefreshKey(prev => prev + 1);
    } catch (err: any) {
      console.error('Failed to empty trash:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      alert(`Failed to empty trash: ${errorMessage}`);
    } finally {
      setEmptyTrashLoading(false);
    }
  };

  return (
    <Box sx={{ mt: { xs: 1, sm: 2, md: 3 }, mb: { xs: 1, sm: 2, md: 3 }, px: { xs: 0, sm: 1 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
        {/* Header with Tabs and Compose Button */}
        <Paper sx={{ mb: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 1, sm: 2 }, flexWrap: 'wrap', gap: 1 }}>
            <Tabs value={folder} onChange={handleFolderChange} variant="scrollable" scrollButtons="auto">
              <Tab 
                label="Inbox" 
                value="inbox" 
                onClick={() => {
                  if (folder === 'inbox') {
                    setViewMode('list');
                    setSelectedMailId(null);
                    setRefreshKey(prev => prev + 1);
                  }
                }}
              />
              <Tab 
                label="Sent" 
                value="sent"
                onClick={() => {
                  if (folder === 'sent') {
                    setViewMode('list');
                    setSelectedMailId(null);
                    setRefreshKey(prev => prev + 1);
                  }
                }}
              />
              <Tab 
                label="Credits / Ad Revenue" 
                value="credits"
                onClick={() => {
                  if (folder === 'credits') {
                    setViewMode('list');
                    setSelectedMailId(null);
                    setRefreshKey(prev => prev + 1);
                  }
                }}
              />
              <Tab 
                label="Debits / Purchases" 
                value="debits"
                onClick={() => {
                  if (folder === 'debits') {
                    setViewMode('list');
                    setSelectedMailId(null);
                    setRefreshKey(prev => prev + 1);
                  }
                }}
              />
              <Tab 
                label="Archive" 
                value="archive"
                onClick={() => {
                  if (folder === 'archive') {
                    setViewMode('list');
                    setSelectedMailId(null);
                    setRefreshKey(prev => prev + 1);
                  }
                }}
              />
              <Tab 
                label="Trash" 
                value="trash"
                onClick={() => {
                  if (folder === 'trash') {
                    setViewMode('list');
                    setSelectedMailId(null);
                    setRefreshKey(prev => prev + 1);
                  }
                }}
              />
            </Tabs>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {folder === 'trash' && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteSweepIcon />}
                  onClick={() => setEmptyTrashDialogOpen(true)}
                  sx={{ minWidth: { xs: 'auto', sm: 'unset' } }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Empty Trash</Box>
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCompose}
                disabled={viewMode === 'compose'}
                sx={{ minWidth: { xs: 'auto', sm: 'unset' } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Compose</Box>
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Main Content Area */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {viewMode === 'list' && (
            <MessageList
              key={`${folder}-${refreshKey}`}
              folder={folder}
              onMessageSelect={handleMessageSelect}
            />
          )}

          {viewMode === 'view' && selectedMailId && (
            <MessageViewer
              mailId={selectedMailId}
              folder={folder}
              onBack={handleBack}
              onReply={handleReply}
              onReplyAll={handleReplyAll}
              onForward={handleForward}
              onDelete={handleDelete}
            />
          )}

          {viewMode === 'compose' && (
            <MessageComposer
              onClose={handleBack}
              onSent={handleSent}
              onDraftSaved={handleDraftSaved}
              replyTo={replyTo}
              composeMode={composeMode}
              initialDraft={selectedDraft}
              initialRecipients={initialComposeRecipients}
            />
          )}
        </Box>
      </Box>

      {/* Empty Trash Confirmation Dialog */}
      <Dialog open={emptyTrashDialogOpen} onClose={() => setEmptyTrashDialogOpen(false)}>
        <DialogTitle>Empty Trash</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete all messages in the trash? This cannot be undone.
            <br /><br />
            <strong>Note:</strong> Transaction records in Account History will be preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmptyTrashDialogOpen(false)} disabled={emptyTrashLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleEmptyTrash} 
            color="error" 
            variant="contained"
            disabled={emptyTrashLoading}
          >
            {emptyTrashLoading ? <CircularProgress size={20} /> : 'Empty Trash'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Messages;

